/**
 * Phase 5 — "Mehr" bottom sheet for the mobile bottom tab bar.
 *
 * Shows all nav routes that are NOT currently pinned in the bottom bar,
 * plus a "+ Funktionen hinzufügen" entry that links to /modules and a
 * Settings footer link.
 *
 * Uses the existing <Sheet side="bottom"> so we get Radix focus-trap +
 * Escape-close for free.
 */

import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, LayoutGrid, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import { ROUTES } from '@/features/route-registry';
import type { RouteModule } from '@/features/types';
import { cn } from '@/lib/utils';

// Routes that appear in the bottom bar (slot 1-4).
// Must be consistent with MobileBottomNav's resolution logic.
function getBarRouteIds(
  tabBarOrder: string[] | null,
): Set<string> {
  const ordered = tabBarOrder
    ? ROUTES.filter((r) => tabBarOrder.includes(r.id)).sort(
        (a, b) => tabBarOrder.indexOf(a.id) - tabBarOrder.indexOf(b.id),
      )
    : ROUTES.filter((r) => (r.nav?.tabBarPriority ?? null) != null).sort(
        (a, b) => (a.nav!.tabBarPriority ?? 0) - (b.nav!.tabBarPriority ?? 0),
      );
  return new Set(ordered.slice(0, 4).map((r) => r.id));
}

export interface MoreSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** User's persisted tab bar order from localStorage (null = use defaults). */
  tabBarOrder: string[] | null;
}

export function MoreSheet({ open, onOpenChange, tabBarOrder }: MoreSheetProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const barIds = getBarRouteIds(tabBarOrder);

  // Routes to show in the sheet: must have nav + not already in the bar.
  // Exclude admin-section routes (section: 'admin') — those belong in Settings.
  const sheetRoutes: RouteModule[] = ROUTES.filter(
    (r) =>
      r.nav &&
      !barIds.has(r.id) &&
      r.nav.section !== 'admin' &&
      !r.nav.superadminOnly &&
      r.kind !== 'public',
  );

  function handleNavigate(path: string) {
    onOpenChange(false);
    navigate(path);
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className={cn(
          'rounded-t-2xl px-0 pb-safe max-h-[80dvh] overflow-y-auto',
          // Override the default SheetContent close button position — we render our own.
          '[&>button]:hidden',
        )}
      >
        <SheetHeader className="px-5 pt-2 pb-4 border-b border-border/50 flex-row items-center justify-between">
          <SheetTitle className="text-base">Mehr Funktionen</SheetTitle>
          <SheetClose asChild>
            <button
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="Schließen"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </SheetClose>
        </SheetHeader>

        {/* Icon grid */}
        <div className="px-4 py-4 grid grid-cols-4 gap-2">
          {sheetRoutes.map((route) => {
            const Icon = route.nav!.icon;
            const isActive = location.pathname.startsWith(route.path);
            return (
              <button
                key={route.id}
                type="button"
                onClick={() => handleNavigate(route.path)}
                aria-current={isActive ? 'page' : undefined}
                className={cn(
                  'flex flex-col items-center gap-1 p-2 rounded-xl min-h-[72px] justify-center',
                  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  isActive ? 'bg-primary/10 text-primary' : 'hover:bg-accent text-muted-foreground hover:text-foreground',
                )}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center',
                    isActive ? 'bg-primary/20' : 'bg-muted',
                  )}
                >
                  <Icon aria-hidden="true" className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-medium text-center leading-tight line-clamp-2 w-full">
                  {route.nav!.label}
                </span>
              </button>
            );
          })}

          {/* "+ Funktionen hinzufügen" tile */}
          <button
            type="button"
            onClick={() => handleNavigate('/modules')}
            className={cn(
              'flex flex-col items-center gap-1 p-2 rounded-xl min-h-[72px] justify-center',
              'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              'hover:bg-accent text-primary',
            )}
            aria-label="Funktionen hinzufügen – Module Browser öffnen"
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center border-2 border-dashed border-primary/40 bg-primary/5">
              <LayoutGrid aria-hidden="true" className="w-5 h-5" />
            </div>
            <span className="text-[10px] font-medium text-center leading-tight">
              + Funktionen
            </span>
          </button>
        </div>

        {/* Footer: Einstellungen */}
        <div className="border-t border-border/50 px-5 py-3">
          <button
            type="button"
            onClick={() => handleNavigate('/settings')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full py-2 rounded-xl hover:bg-accent px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Settings aria-hidden="true" className="w-4 h-4" />
            Einstellungen
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
